import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdAccessTime,
  MdCheckCircle,
  MdHistory,
  MdLocalShipping,
  MdMap,
  MdPlace,
} from 'react-icons/md';
import { DeliveryWorkflowTimeline } from './DeliveryWorkflowTimeline';
import {
  getHistoryLocationLabel,
  getHistoryStatusLabel,
  getOrderStatusLabel,
} from '../../utils/shipment.utils';
import type { Shipment } from '../../types/shipment.types';
import { cn } from '../../lib/utils';

const SERVICE_TYPE_LABELS: Record<string, string> = {
  berth: 'At Berth',
  anchorage: 'Anchorage',
  warehouse: 'Port Store',
};

interface RfqDeliveryStatusSectionProps {
  purchaseOrder: any;
  shipment?: Shipment | null;
}

function DetailCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-[#0088cc]/15 bg-[#0088cc]/5 p-4">
      <div className="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#0088cc]/65">
        {icon}
        {label}
      </div>
      <p className="text-sm font-bold text-[hsl(var(--foreground))]">{value}</p>
    </div>
  );
}

/**
 * Delivery status panel for admin "Your RFQs" details — mirrors customer OrderDetails
 * delivery UX (workflow timeline + shipment location/status/delivered date).
 */
export function RfqDeliveryStatusSection({
  purchaseOrder: po,
  shipment,
}: RfqDeliveryStatusSectionProps) {
  const navigate = useNavigate();
  const dd = po?.deliveryDetails || {};
  const hasAssignment = Boolean(
    po?.hasDeliveryPlan || dd.deliveryRequiredAt || dd.deliveryDestination
  );
  const deliveredAt =
    shipment?.actualDelivery ||
    (dd.workflowStatus === 'delivery_complete'
      ? dd.statusHistory?.find((h: any) => h.key === 'delivery_complete' || h.status === 'delivery_complete')
          ?.changedAt
      : null);

  if (!po || ['draft', 'issued', 'cancelled'].includes(String(po.status || '').toLowerCase())) {
    return null;
  }

  return (
    <div className="mt-6 space-y-6">
      {/* Shipment tracking first */}
      <div className="overflow-hidden rounded-2xl border border-[#0088cc]/20 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-[#0088cc]/15 px-6 py-5">
          <h2 className="flex items-center gap-2 text-base font-bold text-[hsl(var(--foreground))]">
            <MdLocalShipping className="h-5 w-5 text-[#0088cc]" />
            Shipment Tracking
          </h2>
          <button
            type="button"
            onClick={() =>
              navigate(
                `/dashboard/admin/shipments?search=${encodeURIComponent(po.poNumber || '')}`
              )
            }
            className="flex items-center gap-1.5 rounded-lg border border-[#0088cc]/20 px-3 py-1.5 text-xs font-semibold text-[hsl(var(--muted-foreground))] transition-all hover:border-[#0088cc] hover:text-[#0088cc]"
          >
            <MdMap className="h-3.5 w-3.5" />
            View Shipments
          </button>
        </div>

        {!shipment ? (
          <div className="p-12 text-center">
            <MdHistory className="mx-auto mb-3 h-10 w-10 text-[#0088cc]/45" />
            <h3 className="text-sm font-bold text-[hsl(var(--muted-foreground))]">No Shipment Yet</h3>
            <p className="mt-1 text-xs text-[#0088cc]/65">
              Shipment information will appear once tracking is initialized.
            </p>
          </div>
        ) : (
          <div className="space-y-6 p-6">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <DetailCard
                label="Status"
                value={getOrderStatusLabel(shipment)}
                icon={<MdCheckCircle className="h-4 w-4" />}
              />
              <DetailCard
                label="Current Location"
                value={shipment.currentLocation || 'In Transit'}
                icon={<MdMap className="h-4 w-4" />}
              />
              <DetailCard
                label="Est. Delivery"
                value={
                  shipment.estimatedDelivery
                    ? new Date(shipment.estimatedDelivery).toLocaleDateString(undefined, {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })
                    : 'TBD'
                }
                icon={<MdAccessTime className="h-4 w-4" />}
              />
              <DetailCard
                label="Delivered"
                value={
                  shipment.actualDelivery
                    ? new Date(shipment.actualDelivery).toLocaleString(undefined, {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : shipment.status === 'delivered'
                      ? 'Delivered'
                      : 'Not yet'
                }
                icon={<MdLocalShipping className="h-4 w-4" />}
              />
            </div>

            {shipment.trackingHistory && shipment.trackingHistory.length > 0 && (
              <div>
                <h3 className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#0088cc]/65">
                  <MdHistory className="h-4 w-4" />
                  Tracking History
                </h3>
                <div className="space-y-3">
                  {(shipment.trackingHistory || [])
                    .slice()
                    .reverse()
                    .slice(0, 5)
                    .map((entry: any, idx: number) => (
                      <div key={idx} className="flex items-start gap-4">
                        <div className="flex flex-col items-center">
                          <div
                            className={cn(
                              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-white shadow',
                              idx === 0
                                ? 'bg-[#0088cc] text-white'
                                : 'bg-[#0088cc]/10 text-[#0088cc]/65'
                            )}
                          >
                            {idx === 0 ? (
                              <MdCheckCircle className="h-4 w-4" />
                            ) : (
                              <div className="h-2 w-2 rounded-full bg-[#0088cc]/25" />
                            )}
                          </div>
                          {idx < Math.min(4, (shipment.trackingHistory?.length || 0) - 1) && (
                            <div className="mt-1 h-6 w-px bg-[#0088cc]/15" />
                          )}
                        </div>
                        <div className="flex-1 pb-2">
                          <div className="flex items-start justify-between">
                            <p className="text-sm font-semibold text-[hsl(var(--foreground))]">
                              {getHistoryStatusLabel(shipment, entry)}
                            </p>
                            <span className="ml-4 whitespace-nowrap text-[10px] font-medium text-[#0088cc]/65">
                              {entry.timestamp
                                ? new Date(entry.timestamp).toLocaleString()
                                : ''}
                            </span>
                          </div>
                          <p className="mt-0.5 text-xs text-[hsl(var(--muted-foreground))]">
                            {getHistoryLocationLabel(shipment, entry)}
                          </p>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delivery Status second */}
      <div className="overflow-hidden rounded-2xl border border-[#0088cc]/20 bg-white shadow-sm">
        <div className="flex items-center justify-between gap-4 border-b border-[#0088cc]/15 px-6 py-5">
          <h2 className="flex items-center gap-2 text-base font-bold text-[hsl(var(--foreground))]">
            <MdPlace className="h-5 w-5 text-[#0088cc]" />
            Delivery Status
          </h2>
          {po.paymentTermLabel && (
            <span className="rounded-full bg-[#0088cc]/10 px-2.5 py-1 text-[10px] font-bold uppercase text-[hsl(var(--muted-foreground))]">
              {po.paymentTermLabel}
            </span>
          )}
        </div>

        <div className="space-y-5 p-6">
          {!hasAssignment ? (
            <div className="rounded-xl border border-amber-100 bg-amber-50 p-4 text-sm text-amber-800">
              Delivery instructions have not been assigned yet. Once a delivery plan exists on the
              purchase order, location and progress will appear here.
            </div>
          ) : (
            <>
              {dd.deliveryDestination && <DeliveryWorkflowTimeline po={po} />}

              <div className="space-y-3 rounded-xl border border-[#0088cc]/30 bg-[hsl(var(--card))] p-5">
                <div className="flex items-center gap-2 text-sm font-bold text-[#0088cc]">
                  <MdCheckCircle className="h-4 w-4" />
                  Delivery plan
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-[hsl(var(--muted-foreground))]">Mode</span>
                    <p className="font-semibold">
                      {dd.deliveryRequiredAt === 'vessel'
                        ? 'Vessel Delivery (Alongside)'
                        : 'Port Delivery'}
                    </p>
                  </div>
                  <div>
                    <span className="text-[hsl(var(--muted-foreground))]">Service type</span>
                    <p className="font-semibold">
                      {dd.serviceType
                        ? SERVICE_TYPE_LABELS[dd.serviceType] || dd.serviceType
                        : '—'}
                    </p>
                  </div>
                  <div>
                    <span className="text-[hsl(var(--muted-foreground))]">Workflow status</span>
                    <p className="font-semibold">
                      {po.deliveryStatusLabel || dd.workflowStatus || '—'}
                    </p>
                  </div>
                  <div>
                    <span className="text-[hsl(var(--muted-foreground))]">Delivered</span>
                    <p className="font-semibold">
                      {deliveredAt
                        ? new Date(deliveredAt).toLocaleString(undefined, {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : dd.workflowStatus === 'delivery_complete'
                          ? 'Complete'
                          : 'Not yet'}
                    </p>
                  </div>
                </div>

                {dd.deliveryRequiredAt === 'port' && dd.portName && (
                  <p className="text-sm">
                    <span className="text-[hsl(var(--muted-foreground))]">Where:</span>{' '}
                    <strong>
                      {dd.portName}
                      {dd.portCountry ? `, ${dd.portCountry}` : ''}
                    </strong>
                  </p>
                )}
                {dd.deliveryRequiredAt === 'vessel' && (
                  <>
                    {dd.vesselNextPort && (
                      <p className="text-sm">
                        <span className="text-[hsl(var(--muted-foreground))]">Next port:</span>{' '}
                        <strong>{dd.vesselNextPort}</strong>
                      </p>
                    )}
                    {dd.vesselCurrentLocation && (
                      <p className="text-sm">
                        <span className="text-[hsl(var(--muted-foreground))]">Vessel location:</span>{' '}
                        <strong>{dd.vesselCurrentLocation}</strong>
                      </p>
                    )}
                    {dd.vesselEtaWindow && (
                      <p className="text-sm">
                        <span className="text-[hsl(var(--muted-foreground))]">ETA:</span>{' '}
                        {dd.vesselEtaWindow}
                      </p>
                    )}
                  </>
                )}
                {dd.deliveryDestination === 'port_agent' && dd.portAgentName && (
                  <p className="text-sm">
                    <span className="text-[hsl(var(--muted-foreground))]">Port agent:</span>{' '}
                    <strong>{dd.portAgentName}</strong>
                  </p>
                )}
                {dd.deliveryDestination === 'warehouse' && dd.warehouseSnapshot?.name && (
                  <p className="text-sm">
                    <span className="text-[hsl(var(--muted-foreground))]">Warehouse:</span>{' '}
                    <strong>{dd.warehouseSnapshot.name}</strong>
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
